import type { RegisteredComponent } from "@builder.io/sdk-react";

/**
 * Lazy access to the full page-block registry (`~/hec/builder`).
 *
 * The registry's static import graph is ~360KB of page-only block code
 * (product carousel → product-details, search, swiper, recs tiles). A static
 * `import { customComponents }` from a route component puts all of it on that
 * route's critical client path — fetched, parsed and evaluated during initial
 * load — even though the Builder page body never renders `<Content>` on the
 * client until the page-tree fetch lands (`CrashSafeSsrIsland` paints the SSR
 * snapshot instead). This module lets the client start that import off the
 * critical path and lets the server keep synchronous render access.
 *
 * Contract:
 *  - Server: the catch-all loader `await`s `loadCustomComponents()` before
 *    returning a builder payload, so `getLoadedCustomComponents()` is
 *    non-null by the time SSR renders the page body. (The server bundle is a
 *    single module graph — the await resolves on the microtask queue; there
 *    is no network fetch behind it.)
 *  - Client: the first call to `loadCustomComponents()` fetches the registry
 *    chunk(s); until it resolves `getLoadedCustomComponents()` returns null
 *    and callers render their no-registry state (the island keeps showing
 *    the SSR snapshot, which is exactly what it does while the page-tree
 *    fetch is in flight anyway).
 *
 * NOT for chrome: always-loaded chrome surfaces keep using
 * `chromeCustomComponents` (chrome-registry.ts) statically — chrome genuinely
 * hydrates on first paint and must not wait for a deferred chunk.
 */
let loaded: RegisteredComponent[] | null = null;
let inFlight: Promise<RegisteredComponent[]> | null = null;

export function loadCustomComponents(): Promise<RegisteredComponent[]> {
  if (loaded) return Promise.resolve(loaded);
  // PR #1042 blocker 2: the rejected promise must NOT stay cached in
  // `inFlight` — with `??=` alone, one transient chunk failure (network blip,
  // mid-deploy asset window) made every later call reuse the same rejection,
  // so every SPA navigation to a Builder page rendered the no-registry state
  // for the rest of the session. Clearing on rejection makes the next
  // navigation retry the import; the rejection still propagates so callers
  // can report it (see builder-page-view.tsx).
  inFlight ??= import("~/hec/builder").then(
    mod => {
      loaded = mod.customComponents;
      return mod.customComponents;
    },
    err => {
      inFlight = null;
      throw err;
    },
  );
  return inFlight;
}

export function getLoadedCustomComponents(): RegisteredComponent[] | null {
  return loaded;
}
